import DashShell from "@components/common/DashShell";
import { Crumbs, SpinnerLoader, IconLink, Stack } from "@ucchub/ui";
import breadCrumbs from "@json/common/crumbs.json";
import { useTranslation } from "react-i18next";
import Spacing from "@vfuk/core-spacing";
import CustomerDetails from "@components/customer/CustomerDetails";
import CustomerBanner from "@components/customer/CustomerBanner";
import useCustomerDetails from "@hooks/customer/useCustomerDetails";
import { useEffect } from "react";
import AddProductBlock from "@components/customer/AddProductBlock";
import CustomerError from "@components/customer/CustomerError";
import { OrganisationStatus } from "@components/customer/Customer.types";
import { useNavigate, useParams } from "react-router-dom";
import useProductList from "@hooks/product/useProductList";
import Button from "@vfuk/core-button";
import ProductDetails from "@components/customer/ProductDetails";

const CustomerView = () => {
  const { t } = useTranslation();
  const { customerId } = useParams();
  const navigate = useNavigate();
  const {
    customerDetails,
    isCustomerLoading,
    fetchCustomerDetails,
    customerProducts,
    error,
  } = useCustomerDetails();

  const { productList, fetchProductList, isProductListLoading } =
    useProductList();

  const headText = isCustomerLoading
    ? t("core:customers:loading")
    : customerDetails?.name;

  const isDraft = customerDetails?.status === OrganisationStatus.DRAFT;
  const displayCustomerDetails =
    !isCustomerLoading && !error && customerDetails;

  const displayAddProductCTA =
    customerId && customerDetails && customerProducts?.length === 0;

  useEffect(() => {
    if (customerId) {
      fetchCustomerDetails(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (displayAddProductCTA) {
      fetchProductList();
    }
  }, [displayAddProductCTA]);

  if (isDraft) {
    navigate(`/customers`);
  }

  return (
    <DashShell>
      <Crumbs items={breadCrumbs.view_customers} />
      <CustomerBanner
        pid={customerDetails?.pid}
        headText={headText || ""}
        status={customerDetails?.status}
      />

      {error && <CustomerError error={error} />}

      {isCustomerLoading && (
        <SpinnerLoader
          srText={
            t("core:customers:loading_customer_details") ||
            "Loading customer details"
          }
          text={
            t("core:customers:loading_customer_details") ||
            "Loading customer details"
          }
        />
      )}
      {displayAddProductCTA && (
        <AddProductBlock
          productList={productList}
          isProductsLoading={isProductListLoading}
          customerId={customerId}
        />
      )}
      <Spacing
        spacingLevel={{
          top: 3,
          bottom: 3,
        }}
      >
        {!isDraft && displayCustomerDetails && (
          <CustomerDetails customer={customerDetails} />
        )}
      </Spacing>

      {customerId &&
        customerProducts?.map((product) => (
          <ProductDetails
            key={product.id}
            customerProduct={product}
            customerId={customerId}
          />
        ))}
      <Spacing
        spacingLevel={{
          top: 4,
          bottom: 3,
        }}
      >
        <Stack justifyContent="space-between">
          <IconLink
            icon={{ justify: "left", name: "chevron-left" }}
            text={t("core:customers:back")}
            appearance="secondary"
            customRouterProps={{
              to: "/customers",
            }}
          />
          {customerProducts && customerProducts.length > 0 && (
            <Button
              text={t("core:customers:add_another_product")}
              state="disabled"
            />
          )}
        </Stack>
      </Spacing>
    </DashShell>
  );
};

export default CustomerView;
